import axiosInstance from "../../services/axiosConfig";

export const pipelineService = {
  listItems: async ({ campaignId }) => {
    const cleanCampaignId = String(campaignId || "").trim();
    if (!cleanCampaignId) throw new Error("campaignId is required");
    const response = await axiosInstance.get("/company/pipeline/items", {
      params: { campaignId: cleanCampaignId },
    });
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to load pipeline");
    }
    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  inviteInfluencerManual: async ({
    campaignId,
    name,
    mobile,
    email,
    instagramUsername,
    followers,
    following,
    location,
  }) => {
    const cleanCampaignId = String(campaignId || "").trim();
    const cleanInstagramUsername = String(instagramUsername || "").trim();
    if (!cleanCampaignId || !cleanInstagramUsername) {
      throw new Error("campaignId and instagramUsername are required");
    }
    const response = await axiosInstance.post("/company/pipeline/items/invite-manual", {
      campaignId: cleanCampaignId,
      name: String(name || "").trim(),
      mobile: String(mobile || "").trim(),
      email: String(email || "").trim(),
      instagramUsername: cleanInstagramUsername,
      followers: followers === "" || followers == null ? undefined : Number(followers),
      following: following === "" || following == null ? undefined : Number(following),
      location: String(location || "").trim(),
    });
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to invite influencer");
    }
    return response.data;
  },

  moveStage: async ({ itemId, stageId }) => {
    const cleanItemId = String(itemId || "").trim();
    const cleanStageId = String(stageId || "").trim();
    if (!cleanItemId || !cleanStageId) {
      throw new Error("itemId and stageId are required");
    }
    const response = await axiosInstance.patch(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/stage`,
      { stageId: cleanStageId }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to move pipeline stage");
    }
    return response.data.data;
  },

  getActivity: async ({ itemId }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const response = await axiosInstance.get(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/activity`
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to load activity");
    }
    return Array.isArray(response.data?.data?.activity) ? response.data.data.activity : [];
  },

  getPerformanceSummary: async ({ itemId }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const response = await axiosInstance.get(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/performance-summary`
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to load performance summary");
    }
    return response.data?.data || null;
  },

  addNote: async ({ itemId, text }) => {
    const cleanItemId = String(itemId || "").trim();
    const cleanText = String(text || "").trim();
    if (!cleanItemId || !cleanText) throw new Error("itemId and text are required");
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/activity/note`,
      { text: cleanText }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to add note");
    }
    return Array.isArray(response.data?.data?.activity) ? response.data.data.activity : [];
  },

  updateNegotiation: async ({ itemId, proposedRateInr, deliverables, platform, status }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");

    const payload = {};
    if (proposedRateInr !== undefined) payload.proposedRateInr = proposedRateInr;
    if (deliverables !== undefined) payload.deliverables = deliverables;
    if (platform !== undefined) payload.platform = platform;
    if (status !== undefined) payload.status = status;

    const response = await axiosInstance.patch(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/negotiation`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to update negotiation");
    }
    return response.data.data;
  },

  getNegotiationThread: async ({ itemId }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const response = await axiosInstance.get(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/negotiation/thread`
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to load negotiation thread");
    }
    return response.data.data;
  },

  postNegotiationMessage: async ({ itemId, text, amountInr, deliverablesText }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const payload = {};
    if (text !== undefined) payload.text = text;
    if (amountInr !== undefined) payload.amountInr = amountInr;
    if (deliverablesText !== undefined) payload.deliverablesText = deliverablesText;
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/negotiation/message`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to send negotiation message");
    }
    return response.data.data;
  },

  generateContract: async ({ itemId, paymentTermDays }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const payload = {};
    if (paymentTermDays !== undefined) payload.paymentTermDays = paymentTermDays;
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/contract/generate`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to generate contract");
    }
    return response.data.data;
  },

  signContractAsBrand: async ({ itemId, signedBy }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/contract/sign/brand`,
      { signedBy }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to sign contract");
    }
    return response.data.data;
  },

  confirmContract: async ({ itemId }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/contract/confirm`
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to confirm contract");
    }
    return response.data.data;
  },

  inviteByEmail: async ({ itemId, email, advertisingId, templateId }) => {
    const cleanItemId = String(itemId || "").trim();
    const cleanEmail = String(email || "").trim();
    if (!cleanItemId || !cleanEmail) {
      throw new Error("itemId and email are required");
    }
    const payload = { email: cleanEmail };
    const cleanAdvertisingId = String(advertisingId || "").trim();
    if (cleanAdvertisingId) {
      payload.advertisingId = cleanAdvertisingId;
    }
    const cleanTemplateId = String(templateId || "").trim();
    if (cleanTemplateId) {
      payload.templateId = cleanTemplateId;
    }
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/invite/email`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to send invitation email");
    }
    return response.data.data;
  },

  ensureInstagramDmInviteLink: async ({ itemId }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) {
      throw new Error("itemId is required");
    }
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/invite/instagram-dm-link`
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to load invitation link");
    }
    return response.data.data;
  },

  confirmInstagramDmSent: async ({ itemId, token }) => {
    const cleanItemId = String(itemId || "").trim();
    if (!cleanItemId) throw new Error("itemId is required");
    const payload = {};
    const cleanToken = String(token || "").trim();
    if (cleanToken) payload.token = cleanToken;
    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/confirm/instagram-dm-sent`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to confirm Instagram DM send");
    }
    return response.data.data;
  },

  inviteByInstagramDM: async ({ itemId, body, templateId, commentId }) => {
    const cleanItemId = String(itemId || '').trim();
    const cleanBody = String(body || '').trim();
    if (!cleanItemId || !cleanBody) {
      throw new Error('itemId and body are required');
    }
    const payload = { body: cleanBody };
    const cleanTemplateId = String(templateId || '').trim();
    if (cleanTemplateId) payload.templateId = cleanTemplateId;
    const cleanCommentId = String(commentId || '').trim();
    if (cleanCommentId) payload.commentId = cleanCommentId;

    const response = await axiosInstance.post(
      `/company/pipeline/items/${encodeURIComponent(cleanItemId)}/invite/instagram-dm`,
      payload
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to send Instagram DM');
    }
    return response.data.data;
  },
};

export default pipelineService;

